import { ArrowRight } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIntersectionReveal } from '@/hooks/useIntersectionReveal';
import { serviceImages } from '@/config/serviceImages';
import { ANIMATION } from '@/config/constants';

interface ServiceCardProps {
  image: string;
  srcSet: string;
  title: string;
  description: string;
  route: string;
  index: number;
  imagePosition?: string;
  isVisible: boolean;
}

const ServiceCard = ({
  image,
  srcSet,
  title,
  description,
  route,
  index,
  imagePosition = 'center',
  isVisible,
}: ServiceCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [transform, setTransform] = useState({ x: 0, y: 0 });
  const cardRef = useRef<HTMLAnchorElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTransform({ x: x * 8, y: y * 8 });
  };

  const handleMouseLeave = () => {
    setTransform({ x: 0, y: 0 });
  };

  // Calculate stagger delay based on grid position
  const row = Math.floor(index / 3);
  const col = index % 3;
  const staggerDelay =
    row * ANIMATION.ROW_STAGGER_DELAY_MS + col * ANIMATION.COL_STAGGER_DELAY_MS;

  return (
    <Link
      ref={cardRef}
      to={route}
      className="service-card service-card-vignette relative h-[400px] md:h-[420px] rounded-2xl overflow-hidden cursor-pointer group block"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)',
        transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
        transitionDelay: `${staggerDelay}ms`,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Background Image with blur reveal and parallax */}
      <img
        src={image}
        srcSet={srcSet}
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        alt={`${title} service by Altum Contractors Houston TX - professional roofing`}
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ease-out group-hover:scale-105 ${
          imageLoaded ? 'opacity-100 blur-0' : 'opacity-0 blur-md'
        }`}
        style={{
          objectPosition: imagePosition,
          transform: `translate(${transform.x}px, ${transform.y}px) scale(1.02)`,
          transition: 'transform 0.3s ease-out',
        }}
        loading="lazy"
        onLoad={() => setImageLoaded(true)}
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 transition-all duration-500 group-hover:from-black/85 group-hover:via-black/50" />

      {/* Hover glow effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-primary/20 via-transparent to-transparent" />

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-8 transform transition-transform duration-500 group-hover:translate-y-[-4px]">
        <div className="flex items-center gap-2 mb-3">
          <h3
            className="font-heading font-bold text-white text-2xl transition-all duration-300 group-hover:text-white"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {title}
          </h3>
          <ArrowRight className="w-6 h-6 text-white transition-all duration-500 ease-out group-hover:translate-x-2 group-hover:text-primary" />
        </div>

        <p
          className="font-body text-white/90 text-base leading-relaxed max-w-[90%] transition-all duration-300 group-hover:text-white"
          style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
        >
          {description}
        </p>

        <div className="mt-4 h-0.5 bg-gradient-to-r from-primary to-primary/50 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
      </div>
    </Link>
  );
};

const ServicesSection = () => {
  const { t } = useTranslation('home');
  const { ref: sectionRef, isVisible } = useIntersectionReveal<HTMLDivElement>();

  const services = [
    {
      ...serviceImages.roofRepairs,
      title: t('services.roofRepairs.title'),
      description: t('services.roofRepairs.description'),
      route: '/services/roof-repairs',
      imagePosition: 'center 35%',
    },
    {
      ...serviceImages.roofReplacement,
      title: t('services.roofReplacement.title'),
      description: t('services.roofReplacement.description'),
      route: '/services/roof-replacement',
      imagePosition: 'center',
    },
    {
      ...serviceImages.roofMaintenance,
      title: t('services.maintenance.title'),
      description: t('services.maintenance.description'),
      route: '/services/roof-maintenance',
      imagePosition: 'center',
    },
    {
      ...serviceImages.newConstruction,
      title: t('services.newConstruction.title'),
      description: t('services.newConstruction.description'),
      route: '/services/new-construction',
      imagePosition: 'center',
    },
    {
      ...serviceImages.skylightsVentilation,
      title: t('services.enhancements.title'),
      description: t('services.enhancements.description'),
      route: '/services/skylights-ventilation',
      imagePosition: 'center',
    },
  ];

  return (
    <section id="services" ref={sectionRef} className="py-20 md:py-28 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="section-eyebrow">{t('services.eyebrow')}</span>
          <h2 className="font-heading font-bold text-navy text-3xl md:text-5xl mb-4">
            {t('services.headline')}
          </h2>
          <p className="font-body text-charcoal/80 text-lg max-w-[700px] mx-auto">
            {t('services.subheadline')}
          </p>
        </div>

        {/* Services Grid - First row of 3 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.slice(0, 3).map((service, index) => (
            <ServiceCard
              key={service.route}
              image={service.src}
              srcSet={service.srcSet}
              title={service.title}
              description={service.description}
              route={service.route}
              index={index}
              imagePosition={service.imagePosition}
              isVisible={isVisible}
            />
          ))}
        </div>

        {/* Second row - 2 cards centered */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 lg:max-w-[calc(66.666%+0.75rem)] lg:mx-auto">
          {services.slice(3).map((service, index) => (
            <ServiceCard
              key={service.route}
              image={service.src}
              srcSet={service.srcSet}
              title={service.title}
              description={service.description}
              route={service.route}
              index={index + 3}
              imagePosition={service.imagePosition}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
